import z from "zod";
import type { DurationUnitType } from "dayjs/plugin/duration";
import { ExprBinary, ExprToken } from "./expression";
import { HFC_LINE_COLORS } from "../../webapp/hfc/utils/config";

type sampleConfigPeriods = "10s" | "1m" | "5m" | "15m" | "1h" | "4h" | "1d";

type TSampleConfig = Record<
  sampleConfigPeriods,
  {
    label: string;
    round: number;
    unit: DurationUnitType;
    seconds: number;
    minBars: number;
    show: number;
  }
>;

export type TSample = keyof typeof sampleConfig;

export const sampleConfig = {
  "10s": {
    label: "10s",
    round: 10,
    unit: "second" as const,
    seconds: 10,
    minBars: 60,
    show: 60 * 10, // 10 minutes (s)
  },
  "1m": {
    label: "1m",
    round: 1,
    unit: "minute" as const,
    seconds: 60,
    minBars: 1000,
    show: 60 * 60, // 1 hour (s)
  },
  "5m": {
    label: "5m",
    round: 5,
    unit: "minute" as const,
    seconds: 300,
    minBars: 750,
    show: 60 * 60 * 4, // 4 hour (s)
  },
  "15m": {
    label: "15m",
    round: 15,
    unit: "minute" as const,
    seconds: 900,
    minBars: 500,
    show: 60 * 60 * 24, // 1 day (s)
  },
  "1h": {
    label: "1h",
    round: 1,
    unit: "hour" as const,
    seconds: 3600,
    minBars: 250,
    show: 60 * 60 * 24 * 7, // 7 days (s)
  },
  "4h": {
    label: "4h",
    round: 4,
    unit: "hour" as const,
    seconds: 14400,
    minBars: 150,
    show: 60 * 60 * 24 * 7, // 7 days (s)
  },
  "1d": {
    label: "1d",
    round: 1,
    unit: "day" as const,
    seconds: 86400,
    minBars: 30,
    show: 60 * 60 * 24 * 30, // 30 days (s)
  },
} satisfies TSampleConfig;

export const defaultIndicatorsState: (TIndicator & { title: string })[] = [
  {
    id: "ma1",
    stateType: "MovingAverage",
    title: "Moving Average",
    show: false,
    period: 8,
    field: "close",
    type: "Simple",
    offset: 0,
    color: "#1E90FF",
  },
  {
    id: "ma2",
    stateType: "MovingAverage",
    title: "Moving Average",
    show: false,
    period: 13,
    field: "close",
    type: "Simple",
    offset: 0,
    color: "#FFA500",
  },
  {
    id: "ma3",
    stateType: "MovingAverage",
    title: "Moving Average",
    show: false,
    period: 21,
    field: "close",
    type: "Simple",
    offset: 0,
    color: "#800080",
  },
  {
    id: "ma4",
    stateType: "MovingAverage",
    title: "Moving Average",
    show: false,
    period: 34,
    field: "close",
    type: "Simple",
    offset: 0,
    color: "#00CED1",
  },
  {
    id: "ma5",
    stateType: "MovingAverage",
    title: "Moving Average",
    show: false,
    period: 55,
    field: "close",
    type: "Simple",
    offset: 0,
    color: "#FFD700",
  },
  {
    id: "ema1",
    stateType: "MovingAverage",
    title: "Exponential Moving Average",
    show: false,
    period: 9,
    field: "close",
    type: "Exponential",
    offset: 0,
    color: "#ff4d4f",
  },
  {
    id: "ema2",
    stateType: "MovingAverage",
    title: "Exponential Moving Average",
    show: false,
    period: 21,
    field: "close",
    type: "Exponential",
    offset: 0,
    color: "#00b894",
  },
  {
    id: "bb",
    stateType: "BollingerBands",
    title: "Bollinger Bands",
    show: false,
    field: "close",
    color: "#f5ad1c",
    period: 20,
  },
  {
    id: "kc",
    stateType: "KeltnerChannels",
    title: "Keltner Channels",
    show: false,
    field: "close",
    color: "#a133a3",
    period: 20,
  },
  {
    id: "dc",
    stateType: "DonchianChannels",
    title: "Donchian Channels",
    show: false,
    field: "close",
    color: "#1abc9c",
    period: 20,
  },
  {
    id: "rsi",
    stateType: "RSI",
    title: "RSI",
    show: false,
    field: "close",
    color: "#e67e22",
    period: 14,
    upperBound: 70,
    lowerBound: 30,
    upperBoundColor: "#c0392b",
    lowerBoundColor: "#27ae60",
  },
  {
    id: "stochastic",
    stateType: "Stochastic",
    title: "Stochastic",
    show: false,
    field: "close",
    color: "#e74c3c", // dColor
    period: 3, // dPeriod
    kPeriod: 14,
    kColor: "#3498db",
    slowingPeriod: 1,
    upperBound: 80,
    lowerBound: 20,
  },
];

const BollingerBandSchema = z.object({
  stateType: z.literal("BollingerBands"),
  id: z.string(),
  show: z.boolean(),
  field: z.string(),
  color: z.string(),
  period: z.number(),
});
export type TBollingerBand = z.infer<typeof BollingerBandSchema>;

const DonchianChannelsSchema = z.object({
  stateType: z.literal("DonchianChannels"),
  id: z.string(),
  show: z.boolean(),
  field: z.string(),
  color: z.string(),
  period: z.number(),
});
export type TDonchianChannels = z.infer<typeof DonchianChannelsSchema>;

const MovingAverageSchema = z.object({
  stateType: z.literal("MovingAverage"),
  id: z.string(),
  show: z.boolean(),
  period: z.number(),
  field: z.string(),
  type: z.enum(["Simple", "Exponential", "Weighted"]),
  offset: z.number(),
  color: z.string(),
});
export type TMovingAverage = z.infer<typeof MovingAverageSchema>;

const KeltnerChannelsSchema = z.object({
  stateType: z.literal("KeltnerChannels"),
  id: z.string(),
  show: z.boolean(),
  field: z.string(),
  color: z.string(),
  period: z.number(),
});
export type TKeltnerChannels = z.infer<typeof KeltnerChannelsSchema>;

const RsiSchema = z.object({
  stateType: z.literal("RSI"),
  id: z.string(),
  show: z.boolean(),
  field: z.string(),
  color: z.string(),
  period: z.number(),
  upperBound: z.number(),
  lowerBound: z.number(),
  upperBoundColor: z.string(),
  lowerBoundColor: z.string(),
});
export type TRSI = z.infer<typeof RsiSchema>;

const StochasticSchema = z.object({
  stateType: z.literal("Stochastic"),
  id: z.string(),
  show: z.boolean(),
  field: z.string(),
  color: z.string(), // dColor
  period: z.number(), // dPeriod
  kPeriod: z.number(),
  kColor: z.string(),
  slowingPeriod: z.number(),
  upperBound: z.number(),
  lowerBound: z.number(),
});
export type TStochastic = z.infer<typeof StochasticSchema>;

export const indicatorsStateSchema = z.discriminatedUnion("stateType", [
  MovingAverageSchema,
  BollingerBandSchema,
  KeltnerChannelsSchema,
  DonchianChannelsSchema,
  RsiSchema,
  StochasticSchema,
]);

export type TIndicator = z.infer<typeof indicatorsStateSchema>;
export type TIndicatorModifier = TBollingerBand | TDonchianChannels | TMovingAverage | TKeltnerChannels;
export type TBands = "BollingerBands" | "KeltnerChannels" | "DonchianChannels";

export type ChartAggregation = z.infer<typeof MonthSubtractSchema>;

const CurvePropertiesSchema = z.object({
  productId: z.string().optional(),
  periodFrom: z.string().optional(),
  sampleTime: z.enum([...Object.keys(sampleConfig)] as [TSample]).optional(),
  artisType: z.string().optional(),
  editable: z.boolean(),
});
export type TCurveProperties = z.infer<typeof CurvePropertiesSchema>;

const MonthSubtractSchema = z.object({
  type: z.literal("MonthSubtract"),
  enabled: z.boolean(),
  periodFrom: z.string().optional(),
});
export type TMonthSubtract = z.infer<typeof MonthSubtractSchema>;

const ModifiersSchema = z.discriminatedUnion("type", [MonthSubtractSchema]);

export type TModifier = z.infer<typeof ModifiersSchema>;

export const ChartSettingsSchema = z.object({
  id: z.string(),
  idx: z.string().optional(),
  chartType: z.enum(["candlestick", "line"]).optional(),
  ...CurvePropertiesSchema.shape,
  indicatorsState: z.array(indicatorsStateSchema).optional().nullable(),
  modifiers: z.array(ModifiersSchema).optional().nullable(),
  expression: ExprBinary.optional().nullable(),
  // Purely for display reasons to maintain the exact order and parenthesis the user typed. Not used for evaluation.
  expressionSource: z.array(ExprToken).optional().nullable(),
});
export type ChartSettings = z.infer<typeof ChartSettingsSchema>;

// Lenient variant for restoring persisted snapshots: any field that fails validation is dropped (undefined)
// instead of failing the whole parse, so a valid sampleTime survives even if an unrelated field's shape drifted.
export const EphemeralChartSettingsSchema = z.object(
  Object.fromEntries(
    Object.entries(ChartSettingsSchema.shape).map(([key, schema]) => [
      key,
      (schema as z.ZodTypeAny).optional().catch(undefined),
    ]),
  ),
);

const FilterMinMaxSchema = z.object({
  enabled: z.boolean(),
  min: z.number().nullable().optional(),
  max: z.number().nullable().optional(),
});
export type TFilterMinMax = z.infer<typeof FilterMinMaxSchema>;

export const CurveSettingsSchema = z.object({
  filter_min_max: FilterMinMaxSchema,
});
export type TCurveSettings = z.infer<typeof CurveSettingsSchema>;

export const ChartCurveSettingsSchema = z.record(z.string(), CurveSettingsSchema);
export type TChartCurveSettings = z.infer<typeof ChartCurveSettingsSchema>;

export const defaultUserSettings: TUserSettings = {
  wickDownColor: "#d62728",
  wickUpColor: "#2ea043",
  hfcColors: HFC_LINE_COLORS,
  defaultChartType: "candlestick",
  defaultSample: "1m",
  defaultHfcRange: 12,
  alertsEnabled: true,
  warnings: {
    deletePageOne: true,
    deletePageMany: true,
    deleteChartOne: true,
    deleteChartMany: false,
  },
  insertPage: "last",
  insertChart: "last",
  timezone: "Local",
};

export const UserSettingsSchema = z.object({
  wickDownColor: z.string(),
  wickUpColor: z.string(),
  hfcColors: z.record(z.number().or(z.string()), z.string()),
  defaultChartType: z.string(), // "candlestick" | "line", but don't want parsing to fail if we add more types in the future.
  defaultSample: z.string(), // TSample, but don't want parsing to fail if we add more samples in the future.
  defaultHfcRange: z.number(),
  alertsEnabled: z.boolean(),
  warnings: z.object({
    deletePageOne: z.boolean(),
    deletePageMany: z.boolean(),
    deleteChartOne: z.boolean(),
    deleteChartMany: z.boolean(),
  }),
  insertPage: z.enum(["first", "last", "rightOfCurrent"]),
  insertChart: z.enum(["first", "last"]),
  timezone: z.string().optional().default("Local"),
});

export type TUserSettings = z.infer<typeof UserSettingsSchema>;

export const ChartPageType = z.enum(["live", "hfc"]);
export type TChartPageType = z.infer<typeof ChartPageType>;
// Default to "live" if not provided or invalid.
export function parsePageType(input: string | undefined | null) {
  if (!input) return "live";
  const parsed = ChartPageType.safeParse(input);
  if (!parsed.success) return "live";
  return parsed.data;
}

export const sampleByQuestOptions = Object.keys(sampleConfig).map((key) => ({
  label: sampleConfig[key as keyof typeof sampleConfig].label,
  value: key as TSample,
}));

export const sampleToFullLabel = {
  "10s": "10 Seconds",
  "1m": "1 Minute",
  "5m": "5 Minutes",
  "15m": "15 Minutes",
  "1h": "1 Hour",
  "4h": "4 Hours",
  "1d": "1 Day",
};

export const sampleTooltips = {
  "10s": "10 second intervals",
  "1m": "1 minute intervals",
  "5m": "5 minute intervals",
  "15m": "15 minute intervals",
  "1h": "1 hour intervals",
  "4h": "4 hour intervals",
  "1d": "1 day intervals",
} as const satisfies Record<keyof typeof sampleConfig, string>;

export const indicatorsWithPane = ["rsi", "stochastic"] as const;
export const MIN_PANE_HEIGHT = 120;
